import { Fragment, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Flex, Text } from '@chakra-ui/react';

import { KybDocumentType } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { throwOnlyUnhandledErrors } from 'common/hooks/useErrorHandledMutation';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Loader from 'components/Loader';
import { CircleIcon } from 'features/Bento/atoms';
import RichText from 'features/Bento/atoms/RichText';
import { OBCard } from 'features/Bento/components';
import OBFileItem from 'features/Onboarding/components/OBFileItem';
import UploadContent from 'features/Onboarding/modules/ProofOfAddress/components/UploadContent';
import { getUploadKybFunction } from 'features/Storage/upload';

import Layout from '../../Layout';
import {
  LegalRegistrationNumberRequiredFragmentDoc,
  UpdateMissingLegalRegistrationNumberDocument,
} from '../../updateMissingLegalRegistrationNumber.gql';
import { AddOrReplaceRegistrationDocumentDocument } from './addOrReplaceRegistrationDocument.gql';
import { DeleteRegistrationDocumentDocument } from './deleteRegistrationDocument.gql';
import { GetCompanyRegistrationOfficialPaperDocument } from './getCompanyRegistrationOfficialPaper.gql';
import locales from './locales';

const HELP_LINK =
  'https://www.shine.fr/blog/obtenir-avis-situation-repertoire-sirene/';

const DOCUMENT_TYPE = KybDocumentType.CompanyRegistrationOfficialPaper;

const Upload = () => {
  const history = useHistory();
  const { companyProfileId } = useCompanyContext();
  if (!companyProfileId) {
    throw new Error('companyProfileId cannot be undefined');
  }

  const [
    updateMissingLegalRegistrationNumber,
    { error: updateError, loading: updateLoading },
  ] = useMutation(UpdateMissingLegalRegistrationNumberDocument, {
    update(cache, { data }) {
      if (data?.updateCompanyProfile.company?.legalRegistrationNumber) {
        cache.writeFragment({
          data: {
            __typename: 'Company',
            companyProfileId,
            isLegalRegistrationNumberRequired: false,
          },
          fragment: LegalRegistrationNumberRequiredFragmentDoc,
        });
      }
    },
  });
  const { data, loading } = useQuery(
    GetCompanyRegistrationOfficialPaperDocument,
    {
      variables: {
        companyProfileId,
      },
    },
  );
  if (updateError) {
    throwOnlyUnhandledErrors(updateError);
  }
  const [addOrReplaceKybDocument] = useMutation(
    AddOrReplaceRegistrationDocumentDocument,
    {
      refetchQueries: [
        {
          query: GetCompanyRegistrationOfficialPaperDocument,
          variables: {
            companyProfileId,
          },
        },
      ],
    },
  );

  const [deleteRegistrationDocument] = useMutation(
    DeleteRegistrationDocumentDocument,
    {
      refetchQueries: [
        {
          query: GetCompanyRegistrationOfficialPaperDocument,
          variables: {
            companyProfileId,
          },
        },
      ],
    },
  );

  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const legalRegistrationNumber = searchParams.get('legalRegistrationNumber');

  if (!legalRegistrationNumber) {
    throw new Error("legalRegistrationNumber can't be missing");
  }

  if (loading) {
    return <Loader />;
  }

  const kybDocuments = data?.viewer.company.kybDocuments || [];

  const [registrationDocument] = kybDocuments;

  const onContinue = async () => {
    if (!registrationDocument) {
      throw new Error("Shouldn't be allowed to continue without document");
    }

    await updateMissingLegalRegistrationNumber({
      variables: { input: { companyProfileId, legalRegistrationNumber } },
    });
    history.push(`/siret-wall/success`);
  };

  return (
    <Layout allowBack>
      <Flex align="start" direction="column">
        <Heading>{locales.headline}</Heading>
        <Text>{locales.description}</Text>
        <Button
          as="a"
          href={HELP_LINK}
          marginBottom="space-32"
          marginTop="space-16"
          target="_blank"
          variant="inline-primary"
        >
          {locales.helpLink}
        </Button>
        <OBCard width="100%">
          <OBCard.Body paddingY="space-16">
            {registrationDocument ? (
              <OBFileItem
                fileName={locales.fileName}
                onDeleteClick={() =>
                  deleteRegistrationDocument({
                    variables: {
                      input: {
                        kybDocumentId: registrationDocument.kybDocumentId,
                      },
                    },
                  })
                }
                onPreviewClick={() =>
                  registrationDocument.url &&
                  window.open(registrationDocument.url, '_blank')
                }
              />
            ) : (
              <Fragment>
                <UploadContent
                  areFileActionsDisabled
                  documentTypeToUpload={DOCUMENT_TYPE}
                  onSuccessfulUpload={async ({
                    firebaseBucket,
                    firebaseRef,
                  }) => {
                    await addOrReplaceKybDocument({
                      variables: {
                        input: {
                          companyProfileId,
                          firebaseBucket,
                          firebaseRef,
                          type: DOCUMENT_TYPE,
                        },
                      },
                    });
                  }}
                  showUploaderLoading={false}
                  uploadedDocuments={[]}
                  uploadFile={getUploadKybFunction(companyProfileId)}
                />
                <Box paddingX="space-8">
                  <RichText color="grey.-2" marginTop="space-32">
                    {locales.uploadHelper}
                  </RichText>
                  <Flex alignItems="center" marginTop="space-16">
                    <Box paddingRight="space-16">
                      <CircleIcon colorScheme="turquoise" name="check" />
                    </Box>
                    <RichText color="grey.-2">
                      {locales.uploadHelperItem}
                    </RichText>
                  </Flex>
                </Box>
              </Fragment>
            )}
          </OBCard.Body>
          {registrationDocument ? (
            <OBCard.Footer>
              <Button
                isDisabled={updateLoading}
                isLoading={updateLoading}
                onClick={onContinue}
              >
                {locales.continue}
              </Button>
            </OBCard.Footer>
          ) : null}
        </OBCard>
      </Flex>
    </Layout>
  );
};

export default Upload;
